import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { FiSearch, FiPrinter, FiDownload, FiCreditCard } from 'react-icons/fi';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import { colors, borderRadius, shadows } from '../../theme/colors';
import { localDateStr } from '../../utils/localDate';
import { addPrintBrand } from '../../utils/printBranding';

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const fmt = (n) => (Number(n) || 0).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const escHtml = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// One sale's figures after returns / voids (reversedAmount is part of the discount given back).
const rowFigures = (l) => {
  const disc = Number(l.discountAmount) || 0;
  const reversed = l.status === 'Voided' ? disc : Math.min(disc, Number(l.reversedAmount) || 0);
  const bankPct = Number(l.bankSharePct ?? (disc ? (Number(l.bankShareAmount) || 0) / disc * 100 : 0));
  const netDisc = r2(disc - reversed);
  const netBank = r2(netDisc * bankPct / 100);
  return { disc, reversed: r2(reversed), netDisc, netBank, netShop: r2(netDisc - netBank) };
};

const BankDiscountReport = () => {
  const { activeCompany, activeBranch } = useAuth();
  const companyId = activeCompany?.id;
  const today = localDateStr();
  const [dateFrom, setDateFrom] = useState(`${today.slice(0, 7)}-01`);
  const [dateTo, setDateTo] = useState(today);
  const [bank, setBank] = useState('all');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const load = async () => {
    if (!companyId) return;
    if (dateFrom && dateTo && dateTo < dateFrom) { alert('"To" date is before "From" date.'); return; }
    setLoading(true);
    try {
      const q = query(collection(db, 'companies', companyId, 'bankDiscountLog'),
        where('docDate', '>=', dateFrom), where('docDate', '<=', dateTo));
      const snap = await getDocs(q);
      let rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (activeBranch && activeBranch.id && activeBranch.id !== 'all') rows = rows.filter(r => !r.branchId || r.branchId === activeBranch.id);
      rows.sort((a, b) => (a.docDate || '').localeCompare(b.docDate || '') || (a.docNumber || '').localeCompare(b.docNumber || ''));
      setLogs(rows);
      setSearched(true);
    } catch (e) {
      console.error('Bank discount report failed:', e);
      alert('Could not load the report: ' + (e.message || e));
    } finally {
      setLoading(false);
    }
  };

  const banks = useMemo(() => [...new Set(logs.map(l => l.bankName).filter(Boolean))].sort(), [logs]);
  const rows = useMemo(() => (bank === 'all' ? logs : logs.filter(l => l.bankName === bank)), [logs, bank]);

  const totals = useMemo(() => rows.reduce((t, l) => {
    const f = rowFigures(l);
    t.count += l.status === 'Voided' ? 0 : 1;
    t.bill += l.status === 'Voided' ? 0 : (Number(l.billAmount) || 0);
    t.disc += f.disc; t.reversed += f.reversed; t.netDisc += f.netDisc; t.netShop += f.netShop; t.netBank += f.netBank;
    return t;
  }, { count: 0, bill: 0, disc: 0, reversed: 0, netDisc: 0, netShop: 0, netBank: 0 }), [rows]);

  const byBank = useMemo(() => {
    const m = new Map();
    rows.forEach(l => {
      const k = l.bankName || '—';
      const g = m.get(k) || { bank: k, count: 0, bill: 0, netDisc: 0, netShop: 0, netBank: 0 };
      const f = rowFigures(l);
      if (l.status !== 'Voided') { g.count += 1; g.bill += Number(l.billAmount) || 0; }
      g.netDisc += f.netDisc; g.netShop += f.netShop; g.netBank += f.netBank;
      m.set(k, g);
    });
    return [...m.values()].sort((a, b) => a.bank.localeCompare(b.bank));
  }, [rows]);

  const exportCsv = () => {
    const head = ['Date', 'Receipt', 'Branch', 'Bank', 'Card Type', 'Card', 'Discount %', 'Bill', 'Discount', 'Returned/Voided', 'Net Discount', 'Shop Share', 'Bank Share (Claim)', 'Status'];
    const lines = rows.map(l => {
      const f = rowFigures(l);
      return [l.docDate, l.docNumber, l.branchName || '', l.bankName, l.cardType, l.cardMask, l.percent, r2(l.billAmount),
        r2(f.disc), f.reversed, f.netDisc, f.netShop, f.netBank, l.status || 'Posted'];
    });
    const csv = [head, ...lines].map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Bank_Discounts_${dateFrom}_${dateTo}${bank !== 'all' ? '_' + bank.replace(/\W+/g, '_') : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Claim statement to send to the bank (one bank at a time).
  const printClaim = () => {
    if (bank === 'all') { alert('Pick one bank first — the claim statement is per bank.'); return; }
    const claimRows = rows.filter(l => l.status !== 'Voided');
    const body = claimRows.map((l, i) => {
      const f = rowFigures(l);
      return `<tr><td>${i + 1}</td><td>${escHtml(l.docDate)}</td><td>${escHtml(l.docNumber)}</td><td>${escHtml(l.cardType)}</td>
        <td>${escHtml(l.cardMask)}</td><td class="n">${fmt(l.billAmount)}</td><td class="n">${escHtml(l.percent)}%</td>
        <td class="n">${fmt(f.netDisc)}</td><td class="n">${escHtml(l.bankSharePct)}%</td><td class="n"><b>${fmt(f.netBank)}</b></td></tr>`;
    }).join('');
    const tot = claimRows.reduce((t, l) => { const f = rowFigures(l); t.d += f.netDisc; t.b += f.netBank; t.bill += Number(l.billAmount) || 0; return t; }, { d: 0, b: 0, bill: 0 });
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>Bank Discount Claim - ${escHtml(bank)}</title><style>
      body{font-family:'Segoe UI',Arial,sans-serif;margin:24px;color:#1A1A1A;font-size:12px}
      h1{font-size:18px;margin:0 0 4px;color:#800020} .meta{color:#555;margin-bottom:14px}
      table{width:100%;border-collapse:collapse} th,td{border:1px solid #ccc;padding:5px 7px;text-align:left}
      th{background:#800020;color:#fff;font-size:11px;text-transform:uppercase} .n{text-align:right;white-space:nowrap}
      tfoot td{font-weight:700;background:#f5f5f5} .sig{margin-top:50px;display:flex;justify-content:space-between}
      .sig div{border-top:1px solid #333;padding-top:4px;width:220px;text-align:center}
    </style></head><body>
      <h1>Card Discount Claim — ${escHtml(bank)}</h1>
      <div class="meta">${escHtml(activeCompany?.companyName || '')}${activeCompany?.ntn ? ' · NTN ' + escHtml(activeCompany.ntn) : ''}<br>
        Period: ${escHtml(dateFrom)} to ${escHtml(dateTo)} · Transactions: ${claimRows.length}</div>
      <table><thead><tr><th>#</th><th>Date</th><th>Receipt</th><th>Card Type</th><th>Card</th><th class="n">Bill</th><th class="n">Disc %</th>
        <th class="n">Discount</th><th class="n">Bank %</th><th class="n">Claim</th></tr></thead>
      <tbody>${body}</tbody>
      <tfoot><tr><td colspan="5">Total</td><td class="n">${fmt(tot.bill)}</td><td></td><td class="n">${fmt(tot.d)}</td><td></td><td class="n">${fmt(tot.b)}</td></tr></tfoot></table>
      <p style="margin-top:14px">Amount claimed from ${escHtml(bank)}: <b>Rs ${fmt(tot.b)}</b></p>
      <div class="sig"><div>Prepared by</div><div>Authorised signature</div></div>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  return (
    <PageLayout pageTitle="Bank Discount Report" breadcrumb="Reports / Sale Reports / Bank Discounts">
      <Card>
        <Filters>
          <F><L>From</L><I type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></F>
          <F><L>To</L><I type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} /></F>
          <F>
            <L>Bank</L>
            <S value={bank} onChange={e => setBank(e.target.value)} disabled={banks.length === 0}>
              <option value="all">All banks</option>
              {banks.map(b => <option key={b} value={b}>{b}</option>)}
            </S>
          </F>
          <Btns>
            <B type="button" onClick={exportCsv} disabled={rows.length === 0}><FiDownload /> Export CSV</B>
            <B type="button" onClick={printClaim} disabled={rows.length === 0}><FiPrinter /> Claim Statement</B>
            <B type="button" $primary onClick={load} disabled={loading}><FiSearch /> {loading ? 'Loading…' : 'Generate'}</B>
          </Btns>
        </Filters>
      </Card>

      {!searched ? (
        <Empty><FiCreditCard size={34} /><div>Pick a period and click Generate.</div></Empty>
      ) : rows.length === 0 ? (
        <Empty><FiCreditCard size={34} /><div>No bank card discounts in this period.</div></Empty>
      ) : (
        <>
          <Stats>
            <Stat><span>Sales with bank discount</span><b>{totals.count}</b></Stat>
            <Stat><span>Total discount given</span><b>{fmt(totals.netDisc)}</b></Stat>
            <Stat><span>Shop share</span><b>{fmt(totals.netShop)}</b></Stat>
            <Stat $blue><span>Bank share (to claim)</span><b>{fmt(totals.netBank)}</b></Stat>
            {totals.reversed > 0 && <Stat><span>Returned / voided</span><b>{fmt(totals.reversed)}</b></Stat>}
          </Stats>

          <Card>
            <H>By bank</H>
            <TableWrap>
              <T>
                <thead><tr><Th>Bank</Th><Th $r>Sales</Th><Th $r>Bills</Th><Th $r>Discount</Th><Th $r>Shop share</Th><Th $r>Bank share (claim)</Th></tr></thead>
                <tbody>
                  {byBank.map(g => (
                    <tr key={g.bank}>
                      <Td><b>{g.bank}</b></Td><Td $r>{g.count}</Td><Td $r>{fmt(g.bill)}</Td>
                      <Td $r>{fmt(g.netDisc)}</Td><Td $r>{fmt(g.netShop)}</Td><Td $r><b>{fmt(g.netBank)}</b></Td>
                    </tr>
                  ))}
                </tbody>
              </T>
            </TableWrap>
          </Card>

          <Card>
            <H>Sales</H>
            <TableWrap>
              <T>
                <thead><tr>
                  <Th>Date</Th><Th>Receipt</Th><Th>Bank / Card</Th><Th>Card</Th><Th $r>Bill</Th><Th $r>Discount</Th>
                  <Th $r>Returned</Th><Th $r>Shop</Th><Th $r>Bank</Th><Th>Status</Th>
                </tr></thead>
                <tbody>
                  {rows.map(l => {
                    const f = rowFigures(l);
                    return (
                      <tr key={l.id} style={l.status === 'Voided' ? { opacity: 0.55 } : undefined}>
                        <Td>{l.docDate}</Td>
                        <Td>{l.docNumber}{l.branchName ? <Sub>{l.branchName}</Sub> : null}</Td>
                        <Td>{l.bankName} {l.cardType}<Sub>{l.percent}%</Sub></Td>
                        <Td style={{ fontFamily: 'Consolas, monospace', fontSize: 12 }}>{l.cardMask}</Td>
                        <Td $r>{fmt(l.billAmount)}</Td>
                        <Td $r>{fmt(f.disc)}</Td>
                        <Td $r>{f.reversed > 0 ? fmt(f.reversed) : '—'}</Td>
                        <Td $r>{fmt(f.netShop)}</Td>
                        <Td $r><b>{fmt(f.netBank)}</b></Td>
                        <Td>{l.status || 'Posted'}{(l.returnDocNumbers || []).length ? <Sub>Return: {(l.returnDocNumbers || []).join(', ')}</Sub> : null}</Td>
                      </tr>
                    );
                  })}
                </tbody>
              </T>
            </TableWrap>
          </Card>
        </>
      )}
    </PageLayout>
  );
};

const Card = styled.div`background: white; border-radius: ${borderRadius.lg}; box-shadow: ${shadows.sm}; padding: 16px; margin-bottom: 16px;`;
const Filters = styled.div`display: flex; gap: 14px; align-items: flex-end; flex-wrap: wrap;`;
const F = styled.div`display: flex; flex-direction: column; gap: 5px; min-width: 160px;`;
const L = styled.label`font-size: 12px; font-weight: 600; color: ${colors.text.secondary}; text-transform: uppercase;`;
const I = styled.input`padding: 9px 11px; border: 1px solid ${colors.neutral[300]}; border-radius: ${borderRadius.md}; font-size: 14px;`;
const S = styled.select`padding: 9px 11px; border: 1px solid ${colors.neutral[300]}; border-radius: ${borderRadius.md}; font-size: 14px; background: white;`;
const Btns = styled.div`display: flex; gap: 10px; margin-left: auto; flex-wrap: wrap;`;
const B = styled.button`
  display: inline-flex; align-items: center; gap: 6px; padding: 9px 16px; border-radius: ${borderRadius.md};
  font-size: 14px; font-weight: 500; cursor: pointer; border: none;
  background: ${p => (p.$primary ? colors.primary.main : colors.neutral[100])};
  color: ${p => (p.$primary ? 'white' : colors.text.primary)};
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;
const Empty = styled.div`
  background: white; border-radius: ${borderRadius.lg}; box-shadow: ${shadows.sm}; padding: 50px;
  text-align: center; color: ${colors.text.secondary}; display: flex; flex-direction: column; gap: 10px; align-items: center;
`;
const Stats = styled.div`display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 14px; margin-bottom: 16px;`;
const Stat = styled.div`
  background: ${p => (p.$blue ? '#EEF3FF' : 'white')}; border-radius: ${borderRadius.md}; box-shadow: ${shadows.sm};
  padding: 14px 16px; display: flex; flex-direction: column; gap: 4px;
  & span { font-size: 12px; color: ${colors.text.secondary}; }
  & b { font-size: 19px; color: ${p => (p.$blue ? '#1E40AF' : colors.text.primary)}; }
`;
const H = styled.div`font-size: 14px; font-weight: 700; color: ${colors.text.primary}; margin-bottom: 10px;`;
const TableWrap = styled.div`overflow-x: auto;`;
const T = styled.table`width: 100%; border-collapse: collapse;`;
const Th = styled.th`
  text-align: ${p => (p.$r ? 'right' : 'left')}; padding: 9px 12px; font-size: 12px; font-weight: 600; color: ${colors.text.secondary};
  text-transform: uppercase; background: ${colors.neutral[50]}; border-bottom: 1px solid ${colors.neutral[200]}; white-space: nowrap;
`;
const Td = styled.td`
  text-align: ${p => (p.$r ? 'right' : 'left')}; padding: 9px 12px; font-size: 13.5px; border-bottom: 1px solid ${colors.neutral[100]};
  vertical-align: top; ${p => (p.$r ? "font-family: 'Consolas', monospace; font-size: 13px;" : '')}
`;
const Sub = styled.div`font-size: 11.5px; color: ${colors.text.secondary}; margin-top: 2px;`;

export default BankDiscountReport;
